import { useState } from "react";
import { ArrowUpRight, Lock } from "lucide-react";
import { cn } from "@/src/lib/utils";

// ═══════════════════════════════════════════════════════════════════════
// DEMO LIVE PREVIEW
// ═══════════════════════════════════════════════════════════════════════
// Replaces DemoSlideshow. Instead of rotating through abstract mock views,
// this renders the ACTUAL demo inside a scaled iframe. What the prospect
// sees on the detail page is pixel-identical to what they get when they
// click into the live demo.
//
// The iframe loads /?embed=<demoId>&region=<default> — the root app
// short-circuits to the standalone demo shell, so the preview
// shows only the Operating System chrome itself (no duplicate Sloe Laboratory nav).
// The iframe is CSS-scaled to fit the preview container.
//
// Clicking anywhere on the preview triggers the parent's onExploreLive
// callback (same behavior as the "Explore live demo" CTA button, which
// navigates to /explore/:slug — the full-app version of the demo).
// ═══════════════════════════════════════════════════════════════════════

interface DemoLivePreviewProps {
  demoId: string;
  defaultRegion?: string;
  gradient?: string;              // kept for backwards compat; used as loading shimmer bg
  onExploreLive?: () => void;
  productName: string;
}

export default function DemoLivePreview({
  demoId,
  defaultRegion = "bahrain",
  gradient = "from-slate-100 to-slate-200",
  onExploreLive,
  productName,
}: DemoLivePreviewProps) {
  const [loaded, setLoaded] = useState(false);
  const [hovering, setHovering] = useState(false);

  // The iframe renders at 1440x900 (standard desktop), scaled to fit the
  // preview container. 0.55 scale ≈ ~800x500 visible area.
  const SCALE = 0.55;
  const IFRAME_W = 1440;
  const IFRAME_H = 900;

  // Calculated target display size
  const displayW = IFRAME_W * SCALE;
  const displayH = IFRAME_H * SCALE;

  // The preview URL: render the real demo in standalone embed mode
  // (no AppLayout sidebar), with a default region so it skips the region
  // picker gate and shows the Operating System chrome directly.
  const previewUrl = `/?embed=${demoId}&region=${defaultRegion}`;

  return (
    <div
      className={cn(
        "relative aspect-[4/3] rounded-[2rem] overflow-hidden shadow-lg group",
        "bg-gradient-to-br",
        gradient
      )}
      style={{ width: "100%" }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      aria-label={`Preview of ${productName}.`}
    >
      {/* Loading shimmer — visible until iframe fires onLoad */}
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 rounded-full border-2 border-white/30 border-t-white animate-spin"></div>
            <span className="text-white/80 text-xs font-medium tracking-wider uppercase">Loading preview</span>
          </div>
        </div>
      )}

      {/* The iframe — scaled down to fit */}
      <div
        className="absolute inset-0 flex items-center justify-center p-4 sm:p-6"
        style={{ perspective: "1000px" }}
      >
        <div
          className="relative rounded-xl overflow-hidden shadow-2xl border border-white/20 ring-1 ring-black/5 bg-white"
          style={{
            width: displayW,
            height: displayH,
            maxWidth: "95%",
            maxHeight: "90%",
          }}
        >
          {/* Actual iframe sized at full 1440x900, visually scaled */}
          <iframe
            src={previewUrl}
            title={`Live preview of ${productName}`}
            onLoad={() => setLoaded(true)}
            className="border-0 w-full h-full"
            style={{
              width: IFRAME_W,
              height: IFRAME_H,
              transform: `scale(${SCALE})`,
              transformOrigin: "top left",
            }}
            sandbox="allow-same-origin allow-scripts"
            loading="lazy"
          />
        </div>
      </div>

      {/* Hover CTA overlay — appears on hover, and is clickable */}
      <div
        onClick={(e) => {
          e.stopPropagation();
          onExploreLive?.();
        }}
        role="button"
        className={cn(
          "absolute bottom-6 right-6 bg-slate-900 text-white rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2 shadow-xl transition-all z-20 cursor-pointer hover:bg-slate-800",
          hovering
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-2"
        )}
      >
        <span>Open live demo</span>
        <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
      </div>

      {/* Always-visible preview indicator (top-left) */}
      <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-sm text-slate-900 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 shadow-sm z-20 pointer-events-none">
        <Lock className="w-2.5 h-2.5" strokeWidth={2.5} />
        Preview
      </div>
    </div>
  );
}
